import { isAxiosError } from 'axios';
import BarcodeService from 'components/barcode/barcode.service';
import DialogContext from 'components/confirm-dialog-context';
import { FloatingActionsProps } from 'components/floatingactions/FloatingActions';
import TelaFormasPagamentoComponent, {
    getFormaPagamentoAbstracModel,
} from 'components/telaformaspagamento/TelaFormasPagamentoComponent';
import useCatchApiError from 'hooks/useCatchApiError';
import useFlowRoles from 'hooks/useFlowRoles';
import useShowProgress from 'hooks/useShowProgress';
import { useSnackbarCustom } from 'hooks/useSnackCustom';
import { IAbstractModel } from 'model/abstract.model';
import { TipoPessoa } from 'model/cliente.model';
import PdfComprovanteCrediario from 'pages/comprovante-nota-fiscal-outros/PdfComprovanteCrediarios';
import PdfComprovanteTemplate from 'pages/comprovante-nota-fiscal-outros/PdfComprovanteTemplate';
import CpfNota from 'pages/cpfNota/CpfNota';
import {
    FlowEventoNotaEmitidaTypeDTO,
    possuiRejeicao,
} from 'pages/evento-nota-fiscal/evento-nota-fiscal.model';
import EventoNotaFiscal, { StateEventoNotaDialog } from 'pages/evento-nota-fiscal/EventoNotaFiscal';
import { FlowVendaTO, TPagamentoReceivedProps } from 'pages/registra-venda/registra-venda.model';
import RegistraVendaService from 'pages/registra-venda/registra-venda.service';
import CustomIFrame from 'pages/Venda/Header/CustomIFrame';
import RomaneioVenda from 'pages/Venda/RomaneioVenda';
import VendaService from 'pages/Venda/venda.service';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    changeLinearFinalizaVenda,
    completeLoading,
    resetLoading,
} from 'redux/features/blockuiSlice';
import {
    resetDescontoAutorizador,
    setDeposito,
    setTabelaPreco,
} from 'redux/features/configuracoes';
import { resetDesconto } from 'redux/features/descontoFixoSlice';
import {
    addRow,
    addRowDevolucao,
    resetPagamento,
    resetRows,
    SelectedRow,
} from 'redux/features/pagamentoSlice';
import { resetState } from 'redux/features/stateSlice';
import { setActiveStep } from 'redux/features/stepperSlice';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import PlanoContaService from 'service/plano-conta.service';
import { isConsumidor } from 'utils/util';
import { LinhaCupomTroca, RegistroCupomTrocaImpressao, SkuTrocaSerialGerado } from './cupom-troca/cupom-troca.model';
import CupomTrocaService from './cupom-troca/cupom-troca.service';
import { agruparSelecionadosPorPedidoVendaItem, explodirItensParaCupomTroca } from './cupom-troca/cupom-troca.util';
import CupomTrocaImpressao from './cupom-troca/CupomTrocaImpressao';
import CupomTrocaSelecaoDialog from './cupom-troca/CupomTrocaSelecaoDialog';
import {
    DocumentoPosVenda,
    EncerramentoPosVenda,
    exigePedidoCarregado,
    FluxoFinalizacao,
    montarFilaDocumentosPosVenda,
} from './documentos-pos-venda';
import { FlowPedidoCieloIsPagoTO } from './formapagamento/formapagamento.model';
import ModalTroco from './ModalTroco';
import {
    AcaoFinalizarFluxo,
    FinalizarFluxoParam,
    IPagamentoRow,
    RespostaFinalizarVenda,
    TipoInformacaoAdicional,
} from './pagamento.model';

const Pagamento = () => {
    const dispatch = useAppDispatch();
    const { canSalvar, canFinalizar, canFinalizarOutros } = useFlowRoles().venda.pagamento;
    const { canImprimirRomaneio, canImprimirPedido, canImprimirCrediario } =
        useFlowRoles().venda.acoes;
    const { pedido, clientePossuiEnderecoForaEstado } = useAppSelector(s => s.state);
    const isSalvandoPedidoRef = useRef<boolean>(false);
    const { descritivo: descritivoEstabelecimento } = useAppSelector(
        e => e.estabelecimentoSelecionado,
    );
    const location = useLocation();
    const { throwErrorMessage } = useCatchApiError();
    const receivedParams: TPagamentoReceivedProps = location.state;
    const { token: tokenAutorizador } = useAppSelector(
        e => e.configuracoes.configuracaoDescontoAutorizador,
    );
    const configuracaoBalcao = useAppSelector(e => e.configuracoes.configuracaoPedidoBalcao);
    const configuracaoPoliticaVenda = useAppSelector(e => e.configuracoes.configuracaoPoliticaVenda);

    const { quantidadeTotal } = useAppSelector(e => e.state.pedido);
    const { faltaReceber, tableRows, troco } = useAppSelector(s => s.pagamento);
    const faltaReceberRef = useRef<number>(faltaReceber);
    const { valorTotalLiquido } = pedido;

    const initialState = {
        showComprovanteNFOutros: false as boolean,
        showComprovanteCrediario: false as boolean,
        frameDanfeState: {
            open: false as boolean,
            url: '' as string,
        },
        showRomaneio: false as boolean,
        frameBoletoState: false as boolean,
        editRow: null as SelectedRow | null,
        showTrocaModal: false as boolean,
        showCupomTrocaSelecao: {
            open: false as boolean,
            itens: [] as Array<LinhaCupomTroca>,
        },
        showCupomTrocaImpressao: {
            open: false as boolean,
            registros: [] as Array<RegistroCupomTrocaImpressao>,
        },
        loadingCupomTroca: false as boolean,
    };

    const [stateLocal, setStateLocal] = useState(initialState);
    const { cliente } = useAppSelector(e => e.state.pedido);
    const finalizarVendasQtdRef = useRef<number>(0);

    const canEscape = useRef(true);
    const filaDocumentosRef = useRef<Array<DocumentoPosVenda>>([]);
    const respostaFinalizarVendaRef = useRef<RespostaFinalizarVenda | null>(null);
    const pedidoDocumentosRef = useRef<FlowVendaTO | null>(null);
    const pedidoDocumentosPromiseRef = useRef<Promise<FlowVendaTO | null> | null>(null);
    const encerramentoRef = useRef<EncerramentoPosVenda>(
        EncerramentoPosVenda.NAVEGA_REGISTRA_VENDA,
    );

    const onInit = () => {
        dispatch(setActiveStep(1));
    };

    const navigate = useNavigate();
    const { addError, addSuccess, addInfo, closeSnack } = useSnackbarCustom();
    const handleNavigate = useCallback((url: string) => {
        navigate(`/${url}`);
        //eslint-disable-next-line
    }, []);

    const totalRef = useRef<HTMLInputElement>(null);
    const planoContaService = PlanoContaService();

    const getContaTroco = useCallback(() => {
        closeSnack('sem_conta_apropriacao_troco');
        return planoContaService
            .getContaTroco()
            .then(({ data }) => {
                if (!data) {
                    addError({
                        message:
                            'Configure uma conta de apropriação para troco para poder finalizar à venda',
                        closeable: true,
                        persist: true,
                        id: 'sem_conta_apropriacao_troco',
                    });
                    return false;
                }
                return true;
            })
            .catch(err => {
                throwErrorMessage(err);
                return false;
            });
        //eslint-disable-next-line
    }, []);

    const pedidoSomenteBrindes = pedido.itens.length > 0 && pedido.itens.every(item => item.brinde);
    const pedidoTemFreteOuSeguro =
        (pedido.transportador?.valorFrete ?? 0) > 0 || (pedido.transportador?.valorSeguro ?? 0) > 0;
    const podeEmitirNFCe =
        !!cliente &&
        isConsumidor(cliente) &&
        cliente.tipoPessoa !== TipoPessoa.FISICA &&
        !!cliente.consumidorFinal &&
        !pedidoSomenteBrindes &&
        !pedidoTemFreteOuSeguro;

    const floatingActions: FloatingActionsProps = {
        actions: [
            {
                label: 'Salvar pedido (Alt + E)',
                icon: 'fa-solid fa-cart-plus',
                disabled: () => !canSalvar,
                onClick: () => {
                    onClickSalvarVenda();
                },
            },
            {
                disabled: () =>
                    !canFinalizar ||
                    (!!clientePossuiEnderecoForaEstado &&
                        !!pedido.itensTroca.length &&
                        !pedido.itensTroca.some(s => !!s.notaReferenciadaInfo?.notas.length)),
                label: podeEmitirNFCe ? 'Finalizar venda NFC-e' : 'Finalizar venda NF-e',
                icon: 'fa-solid fa-handshake',
                onClick: () => {
                    if (faltaReceberRef.current > 0) {
                        setTimeout(() => {
                            isSalvandoPedidoRef.current = false;
                        }, 1000);
                        return addError({
                            message: 'Existe saldo a receber, verifique!',
                            closeable: true,
                            persist: false,
                        });
                    }
                    onFinalizarFluxo({
                        acao:
                            podeEmitirNFCe ?
                                AcaoFinalizarFluxo.FINALIZAR_VENDA
                                : AcaoFinalizarFluxo.FINALIZAR_VENDA_NFE,
                        request: () =>
                            podeEmitirNFCe ?
                                registraVenda.finalizarVenda(false)
                                : registraVenda.finalizarVendaNFE(true),
                        toCallAfter: iniciarDocumentosVendaFiscal,
                    });
                },
            },
            {
                label: 'Finalizar venda Outros',
                icon: 'fa-solid fa-handshake',
                disabled: () => !canFinalizarOutros,
                onClick: () => {
                    onFinalizarFluxo({
                        acao: AcaoFinalizarFluxo.FINALIZAR_VENDA_OUTROS,
                        request: () => registraVenda.finalizarVendaOutros(false),
                        toCallAfter: iniciarDocumentosVendaOutros,
                        msgFinal: 'Concluindo venda',
                    });
                },
            },
            {
                label: 'CPF na nota? (F10)',
                icon: 'fa-regular fa-file-lines',
                onClick: () => {
                    setOpenCpf(true);
                },
            },
        ],
    };

    const handleKeyPress = (e: KeyboardEvent) => {
        switch (e.key) {
            case 'F9':
                e.preventDefault();
                totalRef.current && totalRef.current.focus();
                break;
            case 'e':
                if (e.altKey) {
                    e.preventDefault();
                    onClickSalvarVenda();
                }
                break;
            case 'F10':
                e.preventDefault();
                setOpenCpf(true);
                break;
        }
    };

    const setFormasPagmentoInicialDevolucao = (
        formasAceitas?: Array<IAbstractModel>,
        formasTroca?: Array<IAbstractModel>,
    ) => {
        if (!formasTroca || !formasAceitas || pedido.itensTroca.length === 0) return;

        const jaAdicionouTroca = tableRows.some(
            row =>
                (row.name === 'NOTA' && row.formaPagamento.descritivo === 'Nota de crédito') ||
                (row.name === 'OUTROS' && row.formaPagamento.descritivo === 'OUTROS DEVOLUÇÃO'),
        );
        if (jaAdicionouTroca) return;

        const trocas = pedido.itensTroca;
        const valorTroca = trocas.reduce((_, act) => (_ += act.valorTotal), 0);
        const trocaToAdd: IPagamentoRow = {
            name: 'NOTA',
            descritivoFormaPagamento: 'Nota de crédito',
            formaPagamento: {
                descritivo: 'Nota de crédito',
                uuid: getFormaPagamentoAbstracModel('NOTA', formasTroca).uuid,
            },
            infoAdicional: {
                descritivo: 'DEVOLUÇÃO',
                numeroParcelas: 1,
                tipo: TipoInformacaoAdicional.OUTROS,
            },
            valor: valorTroca,
            editavel: true,
            pago: true,
            numeroDeDias: null,
            vencimento: null,
        };
        const outrosToAdd: IPagamentoRow = {
            name: 'OUTROS',
            descritivoFormaPagamento: 'Outros',
            formaPagamento: {
                descritivo: 'OUTROS DEVOLUÇÃO',
                uuid: getFormaPagamentoAbstracModel('OUTROS', formasTroca).uuid,
            },
            infoAdicional: {
                descritivo: 'VENDA',
                numeroParcelas: 1,
                tipo: TipoInformacaoAdicional.OUTROS,
            },
            valor: valorTroca,
            editavel: true,
            pago: true,
            numeroDeDias: null,
            vencimento: null,
        };
        dispatch(addRowDevolucao(trocaToAdd));
        dispatch(addRow(outrosToAdd));
    };

    const [openCpf, setOpenCpf] = useState(false);

    const handleClickCloseDialog = () => {
        setOpenCpf(!openCpf);
    };

    const registraVenda = RegistraVendaService();

    // Salvar
    const onClickSalvarVenda = () => {
        if (tableRows.some(e => e.uuidOrigem)) {
            return addError({
                message:
                    'Não é possível salvar esse pedido pois há pagamentos efetuados na maquininha.',
                closeable: true,
                persist: true,
                id: '',
            });
        }
        if (!isSalvandoPedidoRef.current) {
            isSalvandoPedidoRef.current = true;
            if (!receivedParams) {
                onFinalizarFluxo({
                    acao: AcaoFinalizarFluxo.SALVAR_PEDIDO,
                    request: registraVenda.salvarVenda,
                    msgFinal: ' ',
                    hasFullLoading: false,
                });
            } else {
                onFinalizarFluxo({
                    acao: AcaoFinalizarFluxo.ATUALIZAR_PEDIDO,
                    request: registraVenda.salvarVenda,
                    msgFinal: 'Pedido atualizado com sucesso',
                    hasFullLoading: false,
                });
            }
        }
    };

    const hasCrediario = (formasPagamento: IPagamentoRow[] | undefined) => {
        return formasPagamento?.some(e =>
            e.formaPagamento.descritivo.toUpperCase().includes('CREDIÁRIO'),
        );
    };

    useEffect(() => {
        if (stateLocal.frameDanfeState.open) {
            document.body.style.overflow = 'hidden';
            document.body.style.height = '100vh';
        } else {
            document.body.style.overflow = 'auto';
            document.body.style.height = 'auto';
        }
    }, [stateLocal.frameDanfeState.open]);

    const initialRef = {
        boletoURLRef: '' as string,
        erroGerarBoletoRef: false as boolean,
        erroImprimirBoletoRef: false as boolean,
        isVendaBoletoRef: false as boolean,
        numero: 0,
    };

    const pedidoFinalizacaoRef = useRef(initialRef);

    useEffect(() => {
        pedidoFinalizacaoRef.current.isVendaBoletoRef = tableRows.some(e => e.name === 'BOLETO');
    }, [tableRows]);

    const showBoletoMessage = useCallback(() => {
        setTimeout(() => {
            addSuccess({
                id: 'success-pedidosalvo',
                message: 'Venda concluída com sucesso',
                severity: 'success',
                persist: true,
                closeable: true,
            });
            if (pedidoFinalizacaoRef.current.isVendaBoletoRef) {
                if (pedidoFinalizacaoRef.current.erroImprimirBoletoRef) {
                    return addInfo({
                        message: 'Boleto(s) gerado(s) porém não foi possivel realizar a impressão',
                        persist: true,
                        closeable: true,
                    });
                }
                return addError({
                    message: 'Não foi possivel gerar o(s) boleto(s)',
                    persist: true,
                    closeable: true,
                });
            }
            pedidoFinalizacaoRef.current.boletoURLRef = '';
            pedidoFinalizacaoRef.current.erroGerarBoletoRef = false;
            pedidoFinalizacaoRef.current.erroImprimirBoletoRef = false;
            pedidoFinalizacaoRef.current.isVendaBoletoRef = false;
        }, 300);
        //eslint-disable-next-line
    }, []);

    const carregarPedidoDocumentos = (): Promise<FlowVendaTO | null> => {
        if (pedidoDocumentosPromiseRef.current) return pedidoDocumentosPromiseRef.current;
        const pedidoUuid = respostaFinalizarVendaRef.current?.pedido?.uuid;
        if (!pedidoUuid) return Promise.resolve(null);
        const promise = vendaService
            .carregaPedido(pedidoUuid)
            .then(({ data }) => {
                pedidoDocumentosRef.current = data;
                return data;
            })
            .catch(() => null);
        pedidoDocumentosPromiseRef.current = promise;
        return promise;
    };

    const abrirNotaFiscal = () => {
        setStateLocal(prevState => ({
            ...prevState,
            frameDanfeState: {
                open: true,
                url: respostaFinalizarVendaRef.current?.urlDanfe ?? '',
            },
        }));
    };

    const onCloseDanfe = () => {
        setStateLocal(prevState => ({
            ...prevState,
            frameDanfeState: {
                open: false,
                url: prevState.frameDanfeState.url,
            },
        }));
        avancarFilaDocumentos();
    };

    const abrirEventoNota = () => {
        const resposta = respostaFinalizarVendaRef.current;
        if (!resposta) {
            avancarFilaDocumentos();
            return;
        }
        setStateEventoNotaDialog({
            open: true,
            info: {
                eventoType: resposta.eventoNotaType,
                eventoEntrada: resposta.eventoNotaFiscalEntrada,
                eventoSaida: resposta.eventoNotaFiscalSaida,
            },
        });
    };

    const onCloseEventoNota = () => {
        avancarFilaDocumentos();
    };

    const abrirBoleto = () => {
        setStateLocal(prevState => ({ ...prevState, frameBoletoState: true }));
    };

    const onCloseBoleto = () => {
        setStateLocal(prevState => ({ ...prevState, frameBoletoState: false }));
        avancarFilaDocumentos();
    };

    const abrirComprovante = () => {
        if (!pedidoDocumentosRef.current) {
            addError({
                message: 'Não foi possível carregar os dados do comprovante',
                closeable: true,
                persist: true,
                id: 'erro-carrega-pedido-comprovante',
                preventDuplicate: true,
            });
            avancarFilaDocumentos();
            return;
        }
        setStateLocal(prevState => ({ ...prevState, showComprovanteNFOutros: true }));
    };

    const closeComprovante = () => {
        setStateLocal(prevState => ({ ...prevState, showComprovanteNFOutros: false }));
        avancarFilaDocumentos();
    };

    const abrirCrediario = () => {
        carregarPedidoDocumentos().then(pedidoCarregado => {
            if (!pedidoCarregado) {
                avancarFilaDocumentos();
                return;
            }
            setStateLocal(prevState => ({ ...prevState, showComprovanteCrediario: true }));
        });
    };

    const onCloseComprovanteCrediario = () => {
        setStateLocal(prevState => ({ ...prevState, showComprovanteCrediario: false }));
        avancarFilaDocumentos();
    };

    const abrirRomaneio = () => {
        carregarPedidoDocumentos().then(pedidoCarregado => {
            if (!pedidoCarregado) {
                addError({
                    message: 'Não foi possivel imprimir o romaneio',
                    closeable: true,
                    id: 'erro-impressao-romaneio',
                    preventDuplicate: true,
                });
                avancarFilaDocumentos();
                return;
            }
            setStateLocal(prevState => ({ ...prevState, showRomaneio: true }));
        });
    };

    const onCloseRomaneio = () => {
        setStateLocal(prevState => ({ ...prevState, showRomaneio: false }));
        avancarFilaDocumentos();
    };

    const abrirCupomTroca = () => {
        carregarPedidoDocumentos().then(pedidoCarregado => {
            const itens = explodirItensParaCupomTroca(pedidoCarregado?.itens ?? []);
            if (itens.length === 0) {
                avancarFilaDocumentos();
                return;
            }
            setStateLocal(prevState => ({
                ...prevState,
                showCupomTrocaSelecao: { open: true, itens },
            }));
        });
    };

    const avancarFilaDocumentos = () => {
        const proximo = filaDocumentosRef.current.shift();
        if (!proximo) return encerrarDocumentosPosVenda();

        switch (proximo) {
            case DocumentoPosVenda.NOTA_FISCAL:
                return abrirNotaFiscal();
            case DocumentoPosVenda.EVENTO_NOTA:
                return abrirEventoNota();
            case DocumentoPosVenda.COMPROVANTE:
                return abrirComprovante();
            case DocumentoPosVenda.BOLETO:
                return abrirBoleto();
            case DocumentoPosVenda.CREDIARIO:
                return abrirCrediario();
            case DocumentoPosVenda.ROMANEIO:
                return abrirRomaneio();
            case DocumentoPosVenda.CUPOM_TROCA:
                return abrirCupomTroca();
        }
    };

    const encerrarDocumentosPosVenda = () => {
        if (encerramentoRef.current === EncerramentoPosVenda.PERMANECE_NO_PAGAMENTO) return;

        if (encerramentoRef.current === EncerramentoPosVenda.NAVEGA_POR_EVENTO_NOTA) {
            const eventoNotaType = respostaFinalizarVendaRef.current?.eventoNotaType;
            finalizarFluxoPagamento(false);
            if (!!eventoNotaType && possuiRejeicao(eventoNotaType)) {
                return navigate('/registra-venda');
            }
            return navigate('/venda', {
                state: { numeroPedido: pedidoFinalizacaoRef.current.numero },
            });
        }

        finalizarFluxoPagamento(true);
        showBoletoMessage();
    };

    const dialog = useContext(DialogContext);

    const onClickFinalizarVenda = async () => {
        if (troco && troco.valor && !troco.tipoTrocoPagar) {
            return setStateLocal(prevState => ({ ...prevState, showTrocaModal: true }));
        }
        if (troco && troco.valor && troco.tipoTrocoPagar) {
            const result = await getContaTroco();
            if (!result) return;
        }

        if (finalizarVendasQtdRef.current > 0) {
            dialog.confirm({
                title: 'Atenção',
                message:
                    'Não é possivel finalizar uma venda com pagamento pendente. Deseja informar forma de pagamento manualmente?',
                onConfirm: () => {
                    finalizarVendasQtdRef.current = 0;
                    const findIndex = tableRows.findIndex(e => !e.pago);
                    const find = tableRows.find(e => !e.pago);
                    if (find) {
                        setStateLocal(prevState => ({
                            ...prevState,
                            editRow: {
                                index: findIndex,
                                row: find,
                            },
                        }));
                    }
                },
            });
        }
        if (tableRows.some(e => e.pago === false)) {
            finalizarVendasQtdRef.current += 1;
            const areNotPago: Array<FlowPedidoCieloIsPagoTO> = tableRows
                .filter(e => e.pago === false)
                .map(e => {
                    return {
                        credenciadoraUUID: e.credenciadoraUUID!,
                        uuidOrigem: e.uuidOrigem!,
                    };
                });
            return registraVenda
                .cartaoIntegracaoArePago(areNotPago)
                .then(({ data }) => {
                    if (data.itensPagamentoTOS.some(e => e.pago === false)) {
                        data.itensPagamentoTOS
                            .filter(e => !e.pago)
                            .forEach(e => {
                                addError({
                                    message: `Não identificamos o pagamento do ${e.descritivoFormaPagamento} , tente novamente`,
                                    closeable: true,
                                    preventDuplicate: true,
                                });
                            });
                    }
                })
                .catch(() => {
                    addError({
                        message:
                            'Ocorreu um erro inesperado. Se o erro persistir, entre em contato com o suporte',
                        persist: true,
                        severity: 'error',
                        preventDuplicate: false,
                        closeable: true,
                    });
                });
        }
        if (faltaReceberRef.current > 0) {
            setTimeout(() => {
                isSalvandoPedidoRef.current = false;
            }, 1000);
            return addError({
                id: 'falta-receber-positivo',
                message: 'Existe saldo a receber, verifique!',
                closeable: true,
                persist: false,
            });
        }
        if (faltaReceberRef.current < 0) {
            setTimeout(() => {
                isSalvandoPedidoRef.current = false;
            }, 1000);
            return addError({
                id: 'falta-receber-negativo',
                message: 'O valor das formas de pagamento não pode ser maior que o total da venda!',
                closeable: true,
                persist: false,
            });
        }
        const pedidoIsBrinde = pedido.itens.every(item => item.brinde);
        if (
            cliente &&
            isConsumidor(cliente) &&
            (cliente.tipoPessoa !== TipoPessoa.FISICA || pedidoIsBrinde)
        ) {
            return onFinalizarFluxo({
                acao: AcaoFinalizarFluxo.FINALIZAR_VENDA_NFE,
                request: () => registraVenda.finalizarVendaNFE(true),
                toCallAfter: iniciarDocumentosVendaFiscal,
            });
        }

        const isConsumidorFinal = !cliente || isConsumidor(cliente);
        const hasFreteOuSeguro =
            (pedido.transportador?.valorFrete ?? 0) > 0 ||
            (pedido.transportador?.valorSeguro ?? 0) > 0;

        if (isConsumidorFinal && hasFreteOuSeguro) {
            return addError({
                message:
                    'Para Nota Fiscal de Consumidor Eletrônica (NFC-e), não deve ser informado valor de frete. Caso deseje incluir frete, emita uma NF-e (modelo 55).',
                closeable: true,
                persist: true,
            });
        }
        onFinalizarFluxo({
            acao: AcaoFinalizarFluxo.FINALIZAR_VENDA,
            request: () => registraVenda.finalizarVenda(false),
            toCallAfter: iniciarDocumentosVendaFiscal,
        });
    };

    const { showProgress: progress } = useShowProgress();

    const showProgress = (
        fullLoading: boolean,
        msgFinal: string = 'Transmitindo documento fiscal',
    ) => {
        progress({
            mensagens:
                fullLoading ?
                    {
                        0: 'Registrando pedido',
                        40: 'Registrando pagamento',
                        88: msgFinal,
                    }
                    : {
                        50: 'Salvando pedido',
                    },
        });
    };

    const vendaService = VendaService();
    const cupomTrocaService = CupomTrocaService();
    const barcodeService = BarcodeService();

    const taliberadoFinalizarEssaPacoca = useRef(true);

    const onFinalizarFluxo = async (params: FinalizarFluxoParam) => {
        taliberadoFinalizarEssaPacoca.current = false;

        respostaFinalizarVendaRef.current = null;

        canEscape.current = false;
        if (troco?.valor && !troco.tipoTrocoPagar) {
            return setStateLocal(prevState => ({ ...prevState, showTrocaModal: true }));
        }
        if (troco && troco.valor && troco.tipoTrocoPagar) {
            const result = await getContaTroco();
            if (!result) return;
        }

        // verifica existe saldo a receber
        if (faltaReceberRef.current > 0) {
            setTimeout(() => {
                isSalvandoPedidoRef.current = false;
            }, 1000);
            addError({
                id: 'falta-receber-positivo',
                message: 'Existe saldo a receber, verifique!',
                closeable: true,
                persist: false,
            });
            return;
        }
        if (faltaReceberRef.current < 0) {
            setTimeout(() => {
                isSalvandoPedidoRef.current = false;
            }, 1000);
            addError({
                id: 'falta-receber-negativo',
                message: 'O valor das formas de pagamento não pode ser maior que o total da venda!',
                closeable: true,
                persist: false,
            });
            return;
        }

        const { request, toCallAfter, msgFinal, hasFullLoading } = params;
        showProgress(hasFullLoading ?? true, msgFinal);
        request()
            .then(({ data }) => {
                switch (params.acao) {
                    case AcaoFinalizarFluxo.SALVAR_PEDIDO:
                        dispatch(changeLinearFinalizaVenda({ progress: 100 }));
                        finalizarFluxoPagamento(true);
                        setTimeout(() => {
                            addSuccess({
                                id: 'success-pedidosalvo',
                                message: 'Pedido Salvo com sucesso',
                                severity: 'success',
                                persist: false,
                                closeable: true,
                            });
                        }, 300);
                        break;

                    case AcaoFinalizarFluxo.ATUALIZAR_PEDIDO:
                        dispatch(changeLinearFinalizaVenda({ progress: 100 }));
                        finalizarFluxoPagamento(true);
                        setTimeout(() => {
                            addSuccess({
                                id: 'update-succeded',
                                message: 'Pedido atualizado com sucesso',
                                severity: 'success',
                                persist: false,
                                closeable: true,
                            });
                        }, 650);
                        break;

                    case AcaoFinalizarFluxo.FINALIZAR_VENDA:
                        if (!toCallAfter) {
                            throw new Error('toCallAfter não pode ser undefined');
                        }
                        dispatch(changeLinearFinalizaVenda({ progress: 100 }));
                        setTimeout(() => {
                            toCallAfter(data);
                        }, 650);
                        break;

                    case AcaoFinalizarFluxo.FINALIZAR_VENDA_NFE:
                        if (!toCallAfter) {
                            throw new Error('toCallAfter não pode ser undefined');
                        }
                        dispatch(changeLinearFinalizaVenda({ progress: 100 }));
                        setTimeout(() => {
                            toCallAfter(data);
                        }, 650);
                        break;

                    case AcaoFinalizarFluxo.FINALIZAR_VENDA_OUTROS:
                        if (!toCallAfter) {
                            throw new Error('toCallAfter não pode ser undefined');
                        }
                        dispatch(changeLinearFinalizaVenda({ progress: 100 }));
                        setTimeout(() => {
                            toCallAfter(data);
                        }, 650);
                        break;
                }

                if (tokenAutorizador) {
                    dispatch(resetDescontoAutorizador());
                }
                setTimeout(() => {
                    dispatch(resetLoading());
                    canEscape.current = true;
                    taliberadoFinalizarEssaPacoca.current = true;
                }, 2000);
                if (configuracaoBalcao.deposito) {
                    dispatch(setDeposito(configuracaoBalcao.deposito));
                }
                if (configuracaoBalcao.tabelaPreco) {
                    dispatch(setTabelaPreco(configuracaoBalcao.tabelaPreco));
                }
            })
            .catch(err => {
                isSalvandoPedidoRef.current = false;
                dispatch(completeLoading());
                throwErrorMessage(err);
            });
    };

    // Evento nota fiscal
    const initialStateEventoNotaDialog: StateEventoNotaDialog = {
        open: false,
        info: {
            eventoType: FlowEventoNotaEmitidaTypeDTO.SEM_EVENTO,
        },
    };

    const [stateEventoNotaDialog, setStateEventoNotaDialog] = useState(
        initialStateEventoNotaDialog,
    );

    const finalizarFluxoPagamento = (deveNavegar: boolean) => {
        dispatch(resetDesconto());
        dispatch(resetState());
        dispatch(resetRows());
        dispatch(completeLoading());
        dispatch(resetPagamento());
        if (deveNavegar) handleNavigate('registra-venda');
    };

    const iniciarDocumentosPosVenda = (
        resposta: RespostaFinalizarVenda,
        fluxo: FluxoFinalizacao,
        crediario: boolean,
    ) => {
        respostaFinalizarVendaRef.current = resposta;
        pedidoFinalizacaoRef.current.boletoURLRef = resposta.boletoURL ?? '';
        pedidoFinalizacaoRef.current.erroGerarBoletoRef = resposta.erroGerarBoleto;
        pedidoFinalizacaoRef.current.erroImprimirBoletoRef = resposta.erroImprimirBoleto;
        pedidoFinalizacaoRef.current.numero = resposta.pedido?.numero ?? 0;

        const { documentos, encerramento } = montarFilaDocumentosPosVenda({
            fluxo,
            eventoNotaType: resposta.eventoNotaType ?? null,
            urlDanfe: resposta.urlDanfe ?? '',
            possuiEventoNotaFiscal:
                !!resposta.eventoNotaFiscalEntrada || !!resposta.eventoNotaFiscalSaida,
            boletoURL: resposta.boletoURL ?? '',
            crediario,
            canImprimirPedido,
            canImprimirCrediario,
            canImprimirRomaneio,
            imprimirRomaneioConfigurado: configuracaoPoliticaVenda?.imprimirRomaneio !== false,
            imprimirCupomTrocaConfigurado: !!configuracaoPoliticaVenda?.imprimirCupomTroca,
            possuiItensParaCupomTroca: explodirItensParaCupomTroca(pedido.itens).length > 0,
        });

        if (encerramento === EncerramentoPosVenda.PERMANECE_NO_PAGAMENTO) {
            addError({
                message:
                    'Não foi possivel obter retorno da Sefaz, consulte a situação na gestão de DFE.',
                closeable: true,
                persist: false,
            });
        }

        encerramentoRef.current = encerramento;
        filaDocumentosRef.current = documentos;
        if (exigePedidoCarregado(documentos)) carregarPedidoDocumentos();
        avancarFilaDocumentos();
    };

    const iniciarDocumentosVendaFiscal = (resposta: RespostaFinalizarVenda) => {
        pedidoDocumentosRef.current = null;
        pedidoDocumentosPromiseRef.current = null;
        iniciarDocumentosPosVenda(resposta, FluxoFinalizacao.FISCAL, resposta.crediario);
    };

    const iniciarDocumentosVendaOutros = (resposta: RespostaFinalizarVenda) => {
        pedidoDocumentosRef.current = null;
        pedidoDocumentosPromiseRef.current = null;
        respostaFinalizarVendaRef.current = resposta;
        dispatch(resetDesconto());
        dispatch(resetState());
        dispatch(resetRows());
        dispatch(resetPagamento());
        // no fluxo Outros o crediário vem dos itens de pagamento do pedido carregado, não da resposta
        carregarPedidoDocumentos().then(pedidoCarregado => {
            dispatch(completeLoading());
            iniciarDocumentosPosVenda(
                resposta,
                FluxoFinalizacao.OUTROS,
                !!hasCrediario(pedidoCarregado?.itensPagamento),
            );
        });
    };

    const handleCancelarCupomTroca = () => {
        setStateLocal(prevState => ({ ...prevState, showCupomTrocaSelecao: { open: false, itens: [] } }));
        avancarFilaDocumentos();
    };

    const montarRegistrosImpressao = (
        registrosGerados: Array<SkuTrocaSerialGerado>,
        descricaoPorPedidoVendaItem: Map<string, string>,
    ): Promise<Array<RegistroCupomTrocaImpressao>> =>
        Promise.all(
            registrosGerados.map(async registro => ({
                codigo: registro.codigo,
                barcodeUrl: await barcodeService
                    .getBarcode(registro.codigo)
                    .then(({ data: barcodeBlob }) => URL.createObjectURL(barcodeBlob))
                    .catch(() => ''),
                descricaoSku: descricaoPorPedidoVendaItem.get(registro.pedidoVendaItemUuid) ?? '',
            })),
        );

    const handleConfirmarCupomTroca = async (selecionados: Array<LinhaCupomTroca>) => {
        const pedidoUuid = respostaFinalizarVendaRef.current?.pedido?.uuid;
        if (!pedidoUuid) return;

        setStateLocal(prevState => ({ ...prevState, loadingCupomTroca: true }));
        const descricaoPorPedidoVendaItem = new Map(
            selecionados.map(item => [item.pedidoVendaItemUuid, item.descricao]),
        );

        let registrosGerados: Array<SkuTrocaSerialGerado>;
        try {
            const { data } = await cupomTrocaService.gerarSkuTrocaSerial(
                pedidoUuid,
                agruparSelecionadosPorPedidoVendaItem(selecionados),
            );
            registrosGerados = data;
        } catch (err) {
            setStateLocal(prevState => ({ ...prevState, loadingCupomTroca: false }));
            return throwErrorMessage(isAxiosError(err) ? err : undefined);
        }

        const registrosImpressao = await montarRegistrosImpressao(registrosGerados, descricaoPorPedidoVendaItem);
        const semBarcode = registrosImpressao.filter(registro => !registro.barcodeUrl).length;

        if (semBarcode > 0) {
            addError({
                id: 'cupom-troca-sem-barcode',
                message: `Os cupons de troca já foram gerados e são válidos, mas não foi possível gerar o código de barras de ${semBarcode} cupom(ns). O código impresso continua válido.`,
                closeable: true,
                persist: true,
            });
        }

        setStateLocal(prevState => ({
            ...prevState,
            loadingCupomTroca: false,
            showCupomTrocaSelecao: { open: false, itens: [] },
            showCupomTrocaImpressao: { open: true, registros: registrosImpressao },
        }));
    };

    // os object urls dos barcodes vivem enquanto o lote estiver em tela: libera ao trocar de lote e ao desmontar
    useEffect(() => {
        const registros = stateLocal.showCupomTrocaImpressao.registros;
        return () => {
            registros.forEach(registro => {
                if (registro.barcodeUrl) URL.revokeObjectURL(registro.barcodeUrl);
            });
        };
    }, [stateLocal.showCupomTrocaImpressao.registros]);

    const handleCloseCupomTrocaImpressao = () => {
        setStateLocal(prevState => ({ ...prevState, showCupomTrocaImpressao: { open: false, registros: [] } }));
        avancarFilaDocumentos();
    };

    const onFinishEditRow = useCallback(() => {
        setStateLocal(prevState => ({ ...prevState, editRow: null }));
    }, []);

    const onShowModalTroco = useCallback(() => {
        setStateLocal(prevState => ({ ...prevState, showTrocaModal: !prevState.showTrocaModal }));
    }, []);

    const openEditTroco = () => {
        setStateLocal(prevState => ({ ...prevState, showTrocaModal: true }));
    };

    return (
        <TelaFormasPagamentoComponent
            onInit={onInit}
            refs={{
                faltaReceberRef,
                canEscape,
            }}
            footer={{
                onClickAvancar: onClickFinalizarVenda,
                floatingActions,
            }}
            handleKeyPress={handleKeyPress}
            setFormasPagamentoDefault={setFormasPagmentoInicialDevolucao}
            quantidadeTotal={quantidadeTotal}
            valorTotalLiquido={valorTotalLiquido}
            editRow={stateLocal.editRow}
            onFinishEditRow={onFinishEditRow}
            showModalTroco={onShowModalTroco}
            openEditTroco={openEditTroco}>
            <CpfNota
                onClose={handleClickCloseDialog}
                open={openCpf}
                data={null}
            />

            {stateLocal.showRomaneio && pedidoDocumentosRef.current !== null && (
                <RomaneioVenda
                    dados={pedidoDocumentosRef.current}
                    onGoBack={onCloseRomaneio}
                />
            )}

            {stateLocal.frameBoletoState && (
                <CustomIFrame
                    url={pedidoFinalizacaoRef.current.boletoURLRef}
                    onClose={onCloseBoleto}
                />
            )}

            {stateLocal.frameDanfeState.open && stateLocal.frameDanfeState.url && (
                <CustomIFrame
                    url={stateLocal.frameDanfeState.url}
                    onClose={onCloseDanfe}
                />
            )}

            <EventoNotaFiscal
                state={stateEventoNotaDialog}
                setState={setStateEventoNotaDialog}
                onClose={onCloseEventoNota}
            />
            {stateLocal.showComprovanteNFOutros && pedidoDocumentosRef.current !== null && (
                <PdfComprovanteTemplate
                    descritivo={descritivoEstabelecimento}
                    flowVendaTO={pedidoDocumentosRef.current}
                    onCloseComprovante={closeComprovante}
                    showPopUp
                    key='pdf-comprovante-template-pagamento'
                />
            )}

            {stateLocal.showComprovanteCrediario && pedidoDocumentosRef.current !== null && (
                <PdfComprovanteCrediario
                    flowVendaTO={pedidoDocumentosRef.current}
                    onCloseComprovante={onCloseComprovanteCrediario}
                    key='pdf-comprovante-crediario-pagamento'
                />
            )}

            {stateLocal.showTrocaModal && <ModalTroco onShowModalTroco={onShowModalTroco} />}

            {stateLocal.showCupomTrocaSelecao.open && (
                <CupomTrocaSelecaoDialog
                    itens={stateLocal.showCupomTrocaSelecao.itens}
                    onCancelar={handleCancelarCupomTroca}
                    onConfirmar={handleConfirmarCupomTroca}
                    loading={stateLocal.loadingCupomTroca}
                />
            )}

            {stateLocal.showCupomTrocaImpressao.open && (
                <CupomTrocaImpressao
                    registros={stateLocal.showCupomTrocaImpressao.registros}
                    estabelecimentoNome={descritivoEstabelecimento}
                    notaInfo={{
                        tipo: podeEmitirNFCe ? 'NFC-e' : 'NF-e',
                        numero:
                            respostaFinalizarVendaRef.current?.eventoNotaFiscalSaida?.numero ?? '',
                        serie:
                            respostaFinalizarVendaRef.current?.eventoNotaFiscalSaida?.serie ?? '',
                        emissao:
                            respostaFinalizarVendaRef.current?.eventoNotaFiscalSaida?.emissao,
                    }}
                    onClose={handleCloseCupomTrocaImpressao}
                />
            )}
        </TelaFormasPagamentoComponent>
    );
};

export default Pagamento;
